import React, { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FinancialDataState } from '@budgeting101/ui-web';
import { z } from 'zod';
import { api } from '../lib/api.js';
import {
  type ActiveWorkspaceBook,
  type WorkspaceBookKind,
  useAuthStore,
} from '../hooks/useAuth.js';
import { t } from '@budgeting101/i18n';

type WorkspaceProfileKind = 'SHARED' | 'CONTROL' | 'OPERATIONS' | 'MONEY';

const WorkspaceContextBookSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  kind: z.enum(['GROUP_OBLIGATION', 'FORMAL_ACCOUNTING', 'SECONDARY_ACCOUNTING']),
  status: z.enum(['ACTIVE', 'SUSPENDED', 'ARCHIVED']),
  readiness: z.enum(['LEGACY_UNREVIEWED', 'CONFIGURING', 'READY', 'BLOCKED']),
  isPrimary: z.boolean(),
  currency: z.string().min(1).nullable(),
});

const WorkspaceContextResponseSchema = z.object({
  workspace: z.object({
    id: z.string().min(1),
    books: z.array(WorkspaceContextBookSchema),
  }),
});

type WorkspaceContextBook = z.infer<typeof WorkspaceContextBookSchema>;

export function hasControlProfile(profileKinds: readonly WorkspaceProfileKind[]): boolean {
  return profileKinds.includes('CONTROL');
}

export function isSharedOnlyProfile(profileKinds: readonly WorkspaceProfileKind[]): boolean {
  return profileKinds.includes('SHARED') && !hasControlProfile(profileKinds);
}

export function isMoneyOnlyProfile(profileKinds: readonly WorkspaceProfileKind[]): boolean {
  return profileKinds.includes('MONEY') && !hasControlProfile(profileKinds);
}

function preferredBookKind(profileKinds: readonly WorkspaceProfileKind[]): WorkspaceBookKind | null {
  if (isSharedOnlyProfile(profileKinds)) return 'GROUP_OBLIGATION';
  if (hasControlProfile(profileKinds)) return 'FORMAL_ACCOUNTING';
  return null;
}

export function selectActiveWorkspaceBook(
  books: readonly WorkspaceContextBook[],
  profileKinds: readonly WorkspaceProfileKind[],
): ActiveWorkspaceBook | null {
  if (isMoneyOnlyProfile(profileKinds)) return null;
  const active = books.filter((book) => book.status === 'ACTIVE');
  const preferredKind = preferredBookKind(profileKinds);
  const profileBooks = preferredKind
    ? active.filter((book) => book.kind === preferredKind)
    : active;
  const selected = profileBooks.find((book) => book.isPrimary) ?? profileBooks[0];
  if (!selected) return null;
  return {
    id: selected.id,
    name: selected.name,
    kind: selected.kind,
    status: selected.status,
    readiness: selected.readiness,
    currency: selected.currency,
  };
}

export function WorkspaceContextGate({ children }: { children: React.ReactNode }) {
  const userId = useAuthStore((state) => state.userId);
  const workspaceId = useAuthStore((state) => state.workspaceId);
  const profileKinds = useAuthStore((state) => state.profileKinds);
  const activeBook = useAuthStore((state) => state.activeBook);
  const contextRevision = useAuthStore((state) => state.contextRevision);
  const setActiveBook = useAuthStore((state) => state.setActiveBook);
  const contextQuery = useQuery({
    queryKey: ['workspace-context', userId, workspaceId, contextRevision],
    queryFn: () => api.get('/workspaces/current', WorkspaceContextResponseSchema),
    enabled: Boolean(userId && workspaceId),
    staleTime: 5 * 60_000,
  });
  const contextMatchesWorkspace = Boolean(
    workspaceId && contextQuery.data?.workspace.id === workspaceId,
  );
  const selectedBook = useMemo(
    () => contextMatchesWorkspace
      ? selectActiveWorkspaceBook(contextQuery.data?.workspace.books ?? [], profileKinds)
      : null,
    [contextMatchesWorkspace, contextQuery.data, profileKinds],
  );

  useEffect(() => {
    if (!contextQuery.isSuccess) return;
    setActiveBook(selectedBook);
  }, [contextQuery.isSuccess, selectedBook, setActiveBook]);

  if (contextQuery.isLoading) {
    return <FinancialDataState status="loading" label={t('ui.workspaceContext')} />;
  }
  if (contextQuery.isError) {
    return (
      <div className="page-shell">
        <FinancialDataState
          status="unavailable"
          label={t('ui.workspaceContext')}
          error={{
            title: t('auth.workspaceDetailsUnavailable'),
            message: t('auth.workspaceDetailsUnverifiedBook'),
          }}
          onRetry={() => void contextQuery.refetch()}
          retrying={contextQuery.isFetching}
        />
      </div>
    );
  }
  if (contextQuery.isSuccess && !contextMatchesWorkspace) {
    return (
      <div className="page-shell">
        <FinancialDataState
          status="unavailable"
          label={t('ui.workspaceContext')}
          error={{
            title: t('auth.workspaceDetailsUnverified'),
            message: t('auth.workspaceDetailsMismatch'),
          }}
          onRetry={() => void contextQuery.refetch()}
          retrying={contextQuery.isFetching}
        />
      </div>
    );
  }
  if (contextQuery.isSuccess && !selectedBook && !isMoneyOnlyProfile(profileKinds)) {
    return (
      <div className="page-shell">
        <FinancialDataState
          status="unavailable"
          label={t('ui.activeBook')}
          error={{
            title: t('auth.noActiveBookAvailable'),
            message: t('auth.noActiveBookExplanation'),
          }}
          onRetry={() => void contextQuery.refetch()}
          retrying={contextQuery.isFetching}
        />
      </div>
    );
  }
  if (contextQuery.isSuccess && activeBook?.id !== selectedBook?.id) {
    return <FinancialDataState status="loading" label={t('ui.workspaceContext')} />;
  }
  return <>{children}</>;
}
